import { createTheme } from '@mui/material/styles';

// Define font families as constants
const FONT_FAMILY_HEADERS = "'Uncut Sans', sans-serif";
const FONT_FAMILY_BODY = "'Lato', sans-serif";

// Define custom font faces
const customFontFaces = `
  @font-face {
    font-family: 'Uncut Sans';
    src: url('/fonts/UncutSans-Bold.woff') format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
`;

// Add custom font faces to document
const style = document.createElement('style');
style.textContent = customFontFaces;
document.head.appendChild(style);

// Define palette
const palette = {
  common: {
    black: '#000120',
    white: '#f8f8f8',
  },
  background: {
    default: '#f8f8f8',
    paper: '#ffffff',
    elevated: '#fafafa',
  },
  grey: {
    40: '#f6f6f6',
    50: '#EFE7DA',
    80: '#eff1f6',
    100: '#5a5855',
    200: '#363638',
    300: '#212121',
  },
  primary: {
    main: "#000120",
    light: "rgba(0, 1, 32, 0.08)", // 8% opacity
    dark: "#000000",
    50: "rgba(0, 1, 32, 0.04)", // 4% opacity for very light backgrounds
    100: "rgba(0, 1, 32, 0.08)", // 8% opacity
    200: "rgba(0, 1, 32, 0.12)", // 12% opacity
    300: "rgba(0, 1, 32, 0.20)", // 20% opacity
    contrastText: "#ffffff",
  },
  secondary: {
    main: '#FDC727',
    light: '#FDDB66',
    dark: '#E4B118',
    50: 'rgba(253, 199, 39, 0.08)',
    100: 'rgba(253, 199, 39, 0.12)',
    contrastText: '#000120',
  },
  error: {
    main: '#F67066',
    light: '#F89B94',
    dark: '#D94E44',
    50: 'rgba(246, 112, 102, 0.08)',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#F6BC66',
    light: '#F8D194',
    dark: '#D99E44',
    50: 'rgba(246, 188, 102, 0.08)',
    contrastText: '#000120',
  },
  info: {
    main: '#668EF6',
    light: '#94B0F8',
    dark: '#446CD9',
    50: 'rgba(102, 142, 246, 0.08)',
    contrastText: '#ffffff',
  },
  success: {
    main: '#8FD359',
    light: '#B0E182',
    dark: '#6FB73C',
    50: 'rgba(143, 211, 89, 0.08)',
    contrastText: '#000120',
  },
  // Platform-specific colors for social media
  platform: {
    twitter: '#1DA1F2',
    facebook: '#4267B2',
    instagram: '#E4405F',
    linkedin: '#0077B5',
    linkedinOrg: '#0077B5',
    tiktok: '#000000',
    threads: '#000000',
    bluesky: '#00D4FF',
    mastodon: '#595AFF',
  },
};

// Define custom shadows for elevation
const customShadows = {
  card: '0px 2px 4px rgba(0, 1, 32, 0.08)',
  cardHover: '0px 4px 12px rgba(0, 1, 32, 0.12)',
  button: '0px 2px 4px rgba(0, 1, 32, 0.16)',
  buttonHover: '0px 4px 8px rgba(0, 1, 32, 0.20)',
};

// Create theme
const theme = createTheme({
  palette,
  customShadows,
  shape: {
    borderRadius: 8,
  },
  spacing: 8, // Base spacing unit
  typography: {
    fontFamily: FONT_FAMILY_BODY,
    // Header styles
    h1: {
      fontFamily: FONT_FAMILY_HEADERS,
      fontSize: '3.5rem',
      fontWeight: 700,
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontFamily: FONT_FAMILY_HEADERS,
      fontSize: '3rem',
      fontWeight: 700,
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontFamily: FONT_FAMILY_HEADERS,
      fontSize: '2.5rem',
      fontWeight: 700,
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h4: {
      fontFamily: FONT_FAMILY_HEADERS,
      fontWeight: 700,
    },
    h5: {
      fontFamily: FONT_FAMILY_HEADERS,
      fontWeight: 700,
    },
    h6: {
      fontFamily: FONT_FAMILY_HEADERS,
      fontWeight: 700,
    },
    // Body text styles
    body1: {
      fontFamily: FONT_FAMILY_BODY,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontFamily: FONT_FAMILY_BODY,
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '@font-face': [
            {
              fontFamily: 'Uncut Sans',
              fontStyle: 'normal',
              fontWeight: 700,
              fontDisplay: 'swap',
              src: `url('/fonts/UncutSans-Bold.woff') format('woff')`,
            },
          ],
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontWeight: 700,
          fontFamily: FONT_FAMILY_BODY,
        },
        containedPrimary: {
          backgroundColor: palette.common.black,
          color: palette.common.white,
          '&:hover': {
            backgroundColor: palette.grey[200],
            color: palette.grey[40] + '!important',
          },
        },
        outlined: {
          color: palette.common.black,
          borderColor: palette.common.black,
        },
        text: {
          color: palette.common.black,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '24px',
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: palette.common.black,
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.common.black,
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: palette.common.black,
          },
        },
      },
    },
  },
});

export default theme;